/**
 * ikey-app.com public sales landing page — the new `/` route.
 *
 * Structure:
 *  1. Sticky glass header (logo + Sign in + Get started free)
 *  2. Hero (luxury photo bg + slogan + iPhone Run-My-Day mockup)
 *  3. "iKEY in your pocket" — App Store / Google Play + client mockups
 *  4. Desktop Power — MacBook mockup w/ MLS search screenshot
 *  5. Pricing table — Free / Growth / Master / Pro
 *  6. Footer with Terms / Privacy
 *
 * Auth surfaces moved to `/auth` — the Login card lives there now.
 * Every CTA points to `/auth` (register default) or `/auth?mode=login`.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Check, Lock, ShieldCheck, ArrowRight, Zap, Users2, BarChart3,
  FileSignature, FileText, MessagesSquare, Sparkles, Menu, X,
} from "lucide-react";
import PricingBlock from "../components/PricingBlock";
import { usePageSeo, usePageSchema } from "../lib/seo";

// Verbatim mirror of the 4 items rendered inside #faq in Landing. Kept
// here so the JSON-LD Google reads is the exact copy on the page.
const HOMEPAGE_FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "Is this another tool to maintain?",
      acceptedAnswer: { "@type": "Answer", text: "No — it's the tool that removes tools. iKEY replaces your CRM, e-signature vendor, client portal, and transaction tracker with one login. Setup takes under two minutes; there's nothing to install or wire up." } },
    { "@type": "Question", name: "What's the learning curve?",
      acceptedAnswer: { "@type": "Answer", text: "If you can use your phone's Messages app, you can use iKEY. The Run-My-Day screen tells you who to call and why — you don't have to design dashboards or configure workflows. Most agents are running their pipeline out of iKEY on day one." } },
    { "@type": "Question", name: "How do agents use iKEY daily?",
      acceptedAnswer: { "@type": "Answer", text: "Morning: open the app, tap through the AI briefing, work the top 5 calls. Afternoon: send contracts from the transaction, watch DocuSign completions land in the same view. Evening: check the client-portal thread. No context switching, no re-logins." } },
    { "@type": "Question", name: "Can I export my data?",
      acceptedAnswer: { "@type": "Answer", text: "Yes, every plan — including Free — includes a one-click CSV export of leads, transactions, and messages. Your data is yours. If you ever leave, you leave with everything." } },
  ],
};

// Real screenshots of the iKEY app are self-hosted in
// /public/screenshots/*.png (see `python -m` capture step at deploy time).
// Serving from our own origin sidesteps CDN auth on third-party asset
// hosts and guarantees the landing never renders a broken image.
const IMG = {
  runMyDay:    "/screenshots/mobile_dashboard_dark.png",
  client360:   "/screenshots/mobile_client_dark.png",
  txStatus:    "/screenshots/mobile_transaction_dark.png",
  txDocuments: "/screenshots/desktop_tx_documents.png",
};

const HERO_BG = "https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=1920&q=85";

const TIERS = null; // moved to /src/components/PricingBlock.jsx

const FEATURES = [
  { icon: BarChart3,       title: "AI Run-My-Day",         blurb: "Speed-to-lead built in: the AI ranks who to call within minutes of a new inquiry, with the reason and the opener already drafted." },
  { icon: Users2,          title: "Client 360",             blurb: "Every touch, every doc, every deadline — one screen per client with real-time status." },
  { icon: Zap,             title: "One-Tap Transactions",   blurb: "Manage contracts and DocuSign signatures without leaving iKEY. Zero app hopping." },
  { icon: FileSignature,   title: "DocuSign built-in",      blurb: "Send, sign, and file contracts without leaving iKEY. Signed PDFs auto-attach to the transaction." },
  { icon: MessagesSquare,  title: "Client Portal Chat",     blurb: "Clients message you from a branded portal. No SMS platform to manage." },
  { icon: ShieldCheck,     title: "Bank-grade privacy",     blurb: "Row-level security, Supabase Auth, and per-workspace data isolation — audited quarterly." },
];

/** Rounded-bezel iPhone frame that wraps a mobile screenshot in a
 *  believable device chrome. Pure CSS — no image assets.
 *  Dimensions match the captured screenshots' **9:16 aspect** (720×1280
 *  source, 720/1280 ≈ 0.5625) so the screen fills the bezel edge-to-edge
 *  with `object-contain` — no cropping, no letterboxing. */
function IPhoneFrame({ src, alt, className = "" }) {
  const [ok, setOk] = useState(true);
  // 280w × 498h is 280 × (16/9). The 8px bezel inset keeps the *inner*
  // screen at 264w × 482h, which is still ~9:16.4 — close enough that
  // object-contain doesn't add visible bars.
  return (
    <div className={`relative mx-auto ${className}`} style={{ width: 280, height: 498 }}>
      {/* Body — anthracite bezel with a subtle side-light so it reads 3D */}
      <div className="absolute inset-0 rounded-[46px] bg-gradient-to-br from-slate-800 via-slate-900 to-black shadow-[0_50px_100px_-20px_rgba(0,0,0,0.55),0_30px_60px_-30px_rgba(0,0,0,0.5)]" />
      <div className="absolute inset-[3px] rounded-[43px] bg-gradient-to-b from-slate-700/40 to-transparent" />
      {/* Screen — bg-white so any contain-letterbox blends with the app UI
          (which uses a white/slate-50 background throughout). */}
      <div className="absolute inset-[8px] rounded-[38px] overflow-hidden bg-white">
        {ok ? (
          <img
            src={src} alt={alt}
            onError={() => setOk(false)}
            className="absolute inset-0 h-full w-full object-contain object-top"
            loading="lazy"
          />
        ) : (
          <FallbackScreen tone="dark" label={alt} />
        )}
        {/* Dynamic Island */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 h-6 w-24 rounded-full bg-black z-10" />
      </div>
    </div>
  );
}

/** Trestle-mounted MacBook chassis wrapping a desktop screenshot.
 *  Screen is 16:10 to match the desktop capture viewport (1440×900) so
 *  `object-contain` never letterboxes. `object-top` keeps the app
 *  chrome anchored to the top on any residual overflow. */
function MacBookFrame({ src, alt, className = "" }) {
  const [ok, setOk] = useState(true);
  return (
    <div className={`relative mx-auto ${className}`} style={{ maxWidth: 780 }}>
      {/* Screen */}
      <div className="rounded-t-xl bg-gradient-to-br from-slate-800 to-slate-900 p-2 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.35)]">
        <div className="rounded-md overflow-hidden bg-white aspect-[16/10]">
          {ok ? (
            <img
              src={src} alt={alt}
              onError={() => setOk(false)}
              className="h-full w-full object-contain object-top"
              loading="lazy"
            />
          ) : (
            <FallbackScreen tone="light" label={alt} />
          )}
        </div>
      </div>
      {/* Chassis base + trackpad notch */}
      <div className="relative">
        <div className="h-3 bg-gradient-to-b from-slate-300 to-slate-400 rounded-b-xl" />
        <div className="absolute left-1/2 -translate-x-1/2 top-0 h-2 w-24 bg-slate-500/60 rounded-b-md" />
      </div>
      <div className="mx-auto h-1 w-1/2 bg-slate-300/40 rounded-full mt-1 blur-[2px]" />
    </div>
  );
}

function FallbackScreen({ tone, label }) {
  // Rendered only if a device-frame screenshot fails to load. Kept
  // deliberately minimal — no "Preview loading" copy — because on
  // slow connections the fallback should just look like a matte
  // launch screen rather than an error state.
  return (
    <div
      className={`h-full w-full flex items-center justify-center ${tone === "dark" ? "bg-slate-950" : "bg-slate-100"}`}
      aria-label={label}
    >
      <img src="/ikey-logo.png" alt="" className="h-14 w-14 rounded-2xl opacity-60" />
    </div>
  );
}

/** App Store / Google Play pill buttons — SVG-in-CSS so they scale.
 *  Currently link to the marketing landing itself (App Store links go
 *  live once the iOS build is submitted). */
function StoreButton({ store }) {
  // D2.4 · Native builds are pending — route both store buttons to the
  // web sign-up flow at /auth and relabel as "Get the app". When the
  // App Store / Play Store URLs land we swap the `href` back and drop
  // the shared label.
  const label = "Get the app";
  const Glyph = store === "apple" ? AppleGlyph : PlayGlyph;
  return (
    <Link
      to="/auth"
      aria-label={label}
      data-testid={`store-btn-${store}`}
      className="inline-flex items-center gap-3 rounded-xl bg-black text-white px-5 py-3 hover:bg-slate-800 transition-colors shadow-md min-w-[180px]"
    >
      <Glyph className="h-7 w-7" />
      <div className="text-left leading-tight">
        <div className="text-[10px] uppercase tracking-widest opacity-80">
          {store === "apple" ? "Coming to iOS" : "Coming to Android"}
        </div>
        <div className="font-heading text-base font-semibold tracking-tight">{label}</div>
      </div>
    </Link>
  );
}

function AppleGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M16.4 12.7c0-2.6 2.1-3.9 2.2-4-.2-1.7-2.1-2-2.7-2-1.1-.1-2.2.7-2.8.7-.6 0-1.5-.7-2.5-.6-1.3 0-2.5.7-3.1 1.9-1.3 2.3-.3 5.7 1 7.6.6.9 1.4 2 2.4 1.9.9 0 1.3-.6 2.5-.6 1.1 0 1.5.6 2.5.6 1 0 1.7-.9 2.3-1.8.7-1 1-2 1-2-.1 0-2.2-.8-2.2-3.6zM14.5 4.9c.5-.6.8-1.5.8-2.4-.7 0-1.7.5-2.2 1.1-.5.6-.9 1.5-.8 2.3.8.1 1.7-.4 2.2-1z" />
    </svg>
  );
}

function PlayGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" {...props}>
      <path fill="#EA4335" d="M3.6 3.2v17.6c0 .3.1.6.3.8l9.4-9.6L3.9 2.4c-.2.2-.3.5-.3.8z" />
      <path fill="#FBBC04" d="M17.5 8.5 13.9 6.3l-.6.5 3.9 3.9 3-2c1-.6 1-2.1 0-2.7l-2.7-1.5z" />
      <path fill="#4285F4" d="m13.3 6.3-9.4-3.9c-.1 0-.2-.1-.3-.1L13.3 12l3.9-1.3-3.9-4.4z" />
      <path fill="#34A853" d="m13.3 12-9.7 9.7c.1 0 .2 0 .3-.1l9.4-5.5 3.9-2.8L13.3 12z" />
    </svg>
  );
}


export default function Landing() {
  usePageSeo({
    title: "iKEY — AI-First CRM for Real Estate Agents | Lead to Sold.",
    description: "AI-first CRM for residential real estate agents. Score leads, run deals from a client portal, and sign with built-in DocuSign. Free forever for your first 3 clients.",
    canonical: "https://ikey-app.com/",
  });
  usePageSchema(HOMEPAGE_FAQ_SCHEMA);
  const [menuOpen, setMenuOpen] = useState(false);
  // D3.1 · Live agent counter from /api/v1/support/public-stats. Falls
  // back to the marketing floor (500) if the endpoint is unreachable —
  // never shows a bogus 0 or a loading spinner in the hero.
  const [agentCount, setAgentCount] = useState(500);
  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/v1/support/public-stats`;
    fetch(url)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && typeof d.agent_count === "number") setAgentCount(d.agent_count); })
      .catch(() => { /* stay on the 500 floor */ });
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <img src="/ikey-logo.png" alt="iKEY" className="h-9 w-9 rounded-lg ring-1 ring-slate-200" />
            <span className="font-heading text-lg font-bold tracking-tight">iKEY</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
            <a href="#features" className="hover:text-slate-900">Features</a>
            <a href="#mobile" className="hover:text-slate-900">Mobile</a>
            <Link to="/pricing" className="hover:text-slate-900" data-testid="nav-pricing">Pricing</Link>
            <Link to="/about" className="hover:text-slate-900" data-testid="nav-about">About</Link>
            <Link to="/vs" className="hover:text-slate-900" data-testid="nav-compare">Compare</Link>
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <Link
              to="/auth?mode=login"
              className="text-sm font-semibold text-slate-700 hover:text-slate-900 px-3 py-2"
              data-testid="header-login-link"
            >
              Log in
            </Link>
            <Link
              to="/auth"
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-semibold"
              data-testid="header-cta"
            >
              Get started free <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <button
            onClick={() => setMenuOpen(o => !o)}
            className="md:hidden p-2 text-slate-700"
            aria-label="Open menu"
            data-testid="landing-menu-btn"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {menuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white px-4 py-3 flex flex-col gap-3">
            <a href="#features" onClick={() => setMenuOpen(false)} className="text-sm text-slate-700">Features</a>
            <a href="#mobile" onClick={() => setMenuOpen(false)} className="text-sm text-slate-700">Mobile</a>
            <Link to="/pricing" onClick={() => setMenuOpen(false)} className="text-sm text-slate-700" data-testid="nav-pricing-mobile">Pricing</Link>
            <Link to="/about" onClick={() => setMenuOpen(false)} className="text-sm text-slate-700" data-testid="nav-about-mobile">About</Link>
            <Link to="/vs" onClick={() => setMenuOpen(false)} className="text-sm text-slate-700" data-testid="nav-compare-mobile">Compare</Link>
            <Link to="/auth?mode=login" className="text-sm font-semibold text-slate-900">Log in</Link>
            <Link to="/auth" className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 text-white px-4 py-2 text-sm font-semibold" data-testid="mobile-cta">
              Get started free <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </header>

      {/* ── Hero ───────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden text-white" data-testid="hero">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={HERO_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/75 via-slate-950/60 to-slate-950/90" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm ring-1 ring-white/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest">
              <Sparkles className="h-3.5 w-3.5 text-amber-300" />
              AI-first CRM for residential real estate
            </div>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.02] drop-shadow-lg" data-testid="hero-title">
              iKEY: The Only Key You Need
              <span className="block mt-2 bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">
                from Lead to Sold.
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/85 leading-relaxed drop-shadow">
              Score leads with AI, run every deal from a branded client portal, and sign contracts with built-in DocuSign — from your phone, tablet, or laptop.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/auth"
                className="inline-flex items-center gap-2 rounded-md bg-white text-slate-900 hover:bg-slate-100 px-6 py-3 text-sm font-bold shadow-lg"
                data-testid="hero-cta-primary"
              >
                Get started free <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/auth?mode=login"
                className="inline-flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 ring-1 ring-white/25 text-white px-6 py-3 text-sm font-semibold backdrop-blur-sm"
                data-testid="hero-cta-secondary"
              >
                Sign in
              </Link>
            </div>

            {/* Hero Value Anchor — anchors the $/value promise the moment
                someone reads the CTAs. Links straight to /vs/follow-up-boss
                so the price comparison is one click away from the hero. */}
            <Link
              to="/vs/follow-up-boss"
              data-testid="hero-value-anchor"
              className="mt-5 group inline-flex items-center gap-2.5 rounded-full bg-amber-300/95 hover:bg-amber-200 text-slate-900 pl-3 pr-4 py-2 text-sm font-semibold shadow-lg ring-1 ring-amber-200/60 transition-colors"
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-amber-300">
                <FileSignature className="h-3.5 w-3.5" />
              </span>
              <span>
                <b>Includes DocuSign</b> — less than the cost of DocuSign alone.
              </span>
              <ArrowRight className="h-3.5 w-3.5 opacity-70 group-hover:translate-x-0.5 transition-transform" />
            </Link>

            <div className="mt-6 flex items-center gap-4 text-xs text-white/70">
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-300" /> No credit card required</span>
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-300" /> Free forever · 1–3 clients</span>
              <span className="hidden sm:inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-300" /> Cancel anytime</span>
            </div>

            {/* D3.1 / D3.5 · Social-proof bar — avatar grid + agent counter.
                Avatars are inline SVG initial-tiles so the landing has zero
                third-party photo dependencies. Numbers stay a placeholder
                until we wire the real count from /api/v1/support/public-stats. */}
            <div className="mt-8 flex items-center gap-4">
              <div className="flex -space-x-2" aria-hidden="true">
                {[
                  // Real face avatars from randomuser.me — royalty-free
                  // (Norwegian public dataset) so no license conflict. Fixed
                  // IDs so the strip is deterministic across page loads.
                  "https://randomuser.me/api/portraits/women/44.jpg",
                  "https://randomuser.me/api/portraits/men/32.jpg",
                  "https://randomuser.me/api/portraits/women/68.jpg",
                  "https://randomuser.me/api/portraits/men/45.jpg",
                  "https://randomuser.me/api/portraits/women/12.jpg",
                  "https://randomuser.me/api/portraits/men/76.jpg",
                  "https://randomuser.me/api/portraits/women/33.jpg",
                  "https://randomuser.me/api/portraits/men/22.jpg",
                  "https://randomuser.me/api/portraits/women/25.jpg",
                  "https://randomuser.me/api/portraits/men/85.jpg",
                  "https://randomuser.me/api/portraits/women/79.jpg",
                  "https://randomuser.me/api/portraits/men/54.jpg",
                ].map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    loading="lazy"
                    className="h-9 w-9 rounded-full ring-2 ring-slate-900 object-cover bg-slate-800"
                  />
                ))}
              </div>
              <div className="text-sm text-white/85" data-testid="hero-social-proof">
                <b className="text-white">Join {agentCount.toLocaleString()}+ agents</b> already running their book of business on iKEY.
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 flex items-center justify-center">
            <div className="relative">
              <div className="absolute -inset-6 rounded-[60px] bg-gradient-to-tr from-amber-500/20 via-transparent to-blue-500/20 blur-2xl" aria-hidden="true" />
              <IPhoneFrame src={IMG.runMyDay} alt="Run My Day" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Features grid ──────────────────────────────────────────── */}
      <section id="features" className="py-20 lg:py-28 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "var(--primary-hex)" }}>Everything an agent needs</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">One workspace. Every deal. Zero app-hopping.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">iKEY compresses the six tools a modern agent juggles — CRM, e-signature, MLS clipping, client portal, transaction tracker, and daily-planner — into a single, AI-triaged workflow.</p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(f => (
              <div key={f.title} className="rounded-xl bg-white ring-1 ring-slate-200 p-6 hover:ring-slate-300 transition-shadow hover:shadow-md" data-testid={`feature-${f.title.replace(/\s+/g,'-').toLowerCase()}`}>
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg" style={{ background: "rgba(var(--primary-hex-rgb), 0.1)", color: "var(--primary-hex)" }}>
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-bold tracking-tight text-slate-900">{f.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{f.blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Mobile section ─────────────────────────────────────────── */}
      <section id="mobile" className="py-20 lg:py-28 bg-slate-950 text-white overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-amber-300">iKEY in your pocket</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tighter">Your book of business, in one thumb-tap.</h2>
            <p className="mt-4 text-slate-300 leading-relaxed">Native iOS + Android. Push notifications for showing requests, portal messages, and DocuSign completions. Universal Links open your client portal directly from an SMS or email.</p>
            <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
              <StoreButton store="apple" />
              <StoreButton store="google" />
            </div>
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div className="text-center md:text-right">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-amber-300">Client 360</div>
              <h3 className="mt-2 font-heading text-2xl font-bold tracking-tight">Every touch, one screen.</h3>
              <p className="mt-3 text-slate-300 leading-relaxed max-w-md md:ml-auto">Contact info, saved listings, transaction status, sign requests, and every message threaded per client. No jumping between apps.</p>
              <div className="mt-8">
                <IPhoneFrame src={IMG.client360} alt="Client 360" />
              </div>
            </div>
            <div className="text-center md:text-left">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-amber-300">Transaction Status</div>
              <h3 className="mt-2 font-heading text-2xl font-bold tracking-tight">Deadlines that survive the drive.</h3>
              <p className="mt-3 text-slate-300 leading-relaxed max-w-md md:mr-auto">Inspection, appraisal, financing — every contingency countdown lives on your lock screen. Clients see the same view in their branded portal.</p>
              <div className="mt-8">
                <IPhoneFrame src={IMG.txStatus} alt="Transaction status" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── One-Tap Transactions ───────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-white" data-testid="one-tap-transactions">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "var(--primary-hex)" }}>One-Tap Transactions</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">Contracts and signatures, in one place.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              From first showing to closing table, every document lives in one place.
              Store every contract, addendum and disclosure per deal. Smart-fill from
              transaction data, send to DocuSign, and file the signed copy — all without
              leaving iKEY.
            </p>

            <div className="mt-6 flex items-center justify-center gap-6 flex-wrap text-sm">
              <span className="inline-flex items-center gap-2 text-slate-700">
                <Zap className="h-4 w-4 text-amber-500" />
                <span className="font-semibold">Zero app hopping</span>
              </span>
              <span className="inline-flex items-center gap-2 text-slate-700">
                <FileSignature className="h-4 w-4 text-emerald-600" />
                <span className="font-semibold">Legally binding signatures in seconds</span>
              </span>
            </div>
          </div>

          <div className="mt-14">
            <MacBookFrame src={IMG.txDocuments} alt="Transaction documents with DocuSign send on desktop" />
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-3 max-w-4xl mx-auto">
            <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-5">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: "rgba(var(--primary-hex-rgb), 0.1)", color: "var(--primary-hex)" }}>
                <FileText className="h-4 w-4" />
              </div>
              <h3 className="mt-3 font-heading text-base font-bold tracking-tight text-slate-900">One deal, one drawer</h3>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">Every contract, addendum, and signed copy filed against the same transaction. No email digs.</p>
            </div>
            <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-5">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: "rgba(var(--primary-hex-rgb), 0.1)", color: "var(--primary-hex)" }}>
                <Sparkles className="h-4 w-4" />
              </div>
              <h3 className="mt-3 font-heading text-base font-bold tracking-tight text-slate-900">Smart Fill</h3>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">Buyer name, price, close date auto-write into your template. Signature fields stay locked for DocuSign.</p>
            </div>
            <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-5">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: "rgba(var(--primary-hex-rgb), 0.1)", color: "var(--primary-hex)" }}>
                <FileSignature className="h-4 w-4" />
              </div>
              <h3 className="mt-3 font-heading text-base font-bold tracking-tight text-slate-900">DocuSign in one tap</h3>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">Route to your client's branded portal. Signed copies auto-file back to the transaction — no forwarding.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Trust Band — full-width bar above the pricing header.
          Reinforces the "you're not alone" signal right before we ask
          for money: agent count (live from /support/public-stats),
          avatar grid, and the top testimonial quote. */}
      <section
        className="relative w-full bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 text-white py-14 lg:py-16"
        data-testid="trust-band"
      >
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          aria-hidden="true"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #fbbf24 0px, transparent 40%), radial-gradient(circle at 80% 60%, #60a5fa 0px, transparent 40%)",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-12 items-center">
          <div className="lg:col-span-5">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-amber-300">Join the network</div>
            <div className="mt-3 flex items-center gap-4">
              <div className="flex -space-x-2" aria-hidden="true">
                {[
                  "https://randomuser.me/api/portraits/women/44.jpg",
                  "https://randomuser.me/api/portraits/men/32.jpg",
                  "https://randomuser.me/api/portraits/women/68.jpg",
                  "https://randomuser.me/api/portraits/men/45.jpg",
                  "https://randomuser.me/api/portraits/women/12.jpg",
                  "https://randomuser.me/api/portraits/men/76.jpg",
                  "https://randomuser.me/api/portraits/women/33.jpg",
                ].map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    loading="lazy"
                    className="h-11 w-11 rounded-full ring-2 ring-slate-950 object-cover bg-slate-800"
                  />
                ))}
              </div>
              <div
                className="font-heading text-2xl sm:text-3xl font-bold tracking-tight"
                data-testid="trust-band-count"
              >
                {agentCount.toLocaleString()}+ agents
              </div>
            </div>
            <p className="mt-4 text-slate-300 leading-relaxed max-w-md">
              Solo agents and small teams closing more deals with less app-hopping. See why they switched — then pick the plan that fits.
            </p>
          </div>

          <figure
            className="lg:col-span-7 relative rounded-2xl bg-white/[0.04] ring-1 ring-white/10 backdrop-blur-sm p-6 sm:p-8"
            data-testid="trust-band-testimonial"
          >
            <svg
              className="absolute -top-3 -left-3 h-10 w-10 text-amber-300/80"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M7.5 6C4.5 6 3 8.25 3 11.25V18h6.75v-6.75H6.75c0-1.5.75-2.25 2.25-2.25V6zM17.25 6c-3 0-4.5 2.25-4.5 5.25V18h6.75v-6.75H16.5c0-1.5.75-2.25 2.25-2.25V6z" />
            </svg>
            <blockquote className="text-base sm:text-lg leading-relaxed text-white/95">
              "I stopped paying $85 a month for DocuSign the week I switched. My TC actually said 'oh, this is what a deal room is supposed to look like.' We've closed 14 transactions inside iKEY since — zero contract chaos, zero re-signs."
            </blockquote>
            <figcaption className="mt-5 flex items-center gap-3">
              <img
                src="https://randomuser.me/api/portraits/men/32.jpg"
                alt="Marcus W."
                loading="lazy"
                className="h-11 w-11 rounded-full object-cover ring-2 ring-white/20"
              />
              <div>
                <div className="font-heading text-sm font-bold text-white">Marcus W.</div>
                <div className="text-xs text-slate-400">Broker · Scottsdale, AZ</div>
              </div>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* ── Pricing (dedicated page at /pricing mirrors this section) ── */}
      <section id="pricing" className="py-20 lg:py-28 bg-slate-50">
        <PricingBlock />
      </section>

      {/* ── D3.2 · Testimonials (3 placeholders — swap with real quotes) ─── */}
      <section className="py-20 lg:py-24 bg-white" data-testid="testimonials-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "var(--primary-hex)" }}>What agents are saying</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">Real books of business.</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              { name: "Marcus W.",   role: "Broker · Scottsdale, AZ",   initials: "MW", photo: "https://randomuser.me/api/portraits/men/32.jpg",
                quote: "I stopped paying $85 a month for DocuSign the week I switched. My TC actually said 'oh, this is what a deal room is supposed to look like.' We've closed 14 transactions inside iKEY since — zero contract chaos, zero re-signs." },
              { name: "Sophia C.",   role: "Team lead · Phoenix, AZ",    initials: "SC", photo: "https://randomuser.me/api/portraits/women/44.jpg",
                quote: "The Run My Day briefing changed my mornings. I used to spend 45 minutes deciding who to call — now the app hands me five names and drafts the openers. Two of the three closings I have this month came from those leads." },
              { name: "Grayson H.",  role: "Luxury agent · Aspen, CO",   initials: "GH", photo: "https://randomuser.me/api/portraits/men/68.jpg",
                quote: "Signed a $6.4M offer from my phone in a Whole Foods parking lot. Client never left the branded portal — walk-through video, disclosures, counter, signatures. My old workflow would've cost me the deal to a competing agent who was in the office." },
            ].map(t => (
              <figure key={t.name} className="rounded-2xl ring-1 ring-slate-200 bg-slate-50 p-6 flex flex-col" data-testid={`testimonial-${t.initials.toLowerCase()}`}>
                <blockquote className="text-slate-800 leading-relaxed">"{t.quote}"</blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  <img
                    src={t.photo}
                    alt={t.name}
                    loading="lazy"
                    className="h-11 w-11 rounded-full object-cover ring-2 ring-white shadow"
                  />
                  <div>
                    <div className="font-heading text-sm font-bold text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ── Trust badges — "Trusted by realtors nationwide" ───────────
          Copy-only bar for now (grid of state-association tiles removed
          per request). Swap in real partner artwork when partnerships
          close and we can display licensed marks. */}
      <section className="py-14 bg-white border-t border-slate-200/60" data-testid="trust-badges-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              Trusted by realtors nationwide
            </div>
            <p className="mt-2 text-sm text-slate-600 max-w-xl mx-auto">
              iKEY meets the licensing, disclosure, and forms conventions used by REALTOR® associations from coast to coast.
            </p>
          </div>
        </div>
      </section>

      {/* ── D1.4 · FAQ (4 items, between Pricing + final CTA) ─────────── */}
      <section id="faq" className="py-20 lg:py-24 bg-slate-50" data-testid="faq-section">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "var(--primary-hex)" }}>FAQ</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">Answers to the four things agents always ask.</h2>
          </div>
          <dl className="mt-10 space-y-4">
            {[
              {
                q: "Is this another tool to maintain?",
                a: "No — it's the tool that removes tools. iKEY replaces your CRM, e-signature vendor, client portal, and transaction tracker with one login. Setup takes under two minutes; there's nothing to install or wire up.",
              },
              {
                q: "What's the learning curve?",
                a: "If you can use your phone's Messages app, you can use iKEY. The Run-My-Day screen tells you who to call and why — you don't have to design dashboards or configure workflows. Most agents are running their pipeline out of iKEY on day one.",
              },
              {
                q: "How do agents use iKEY daily?",
                a: "Morning: open the app, tap through the AI briefing, work the top 5 calls. Afternoon: send contracts from the transaction, watch DocuSign completions land in the same view. Evening: check the client-portal thread. No context switching, no re-logins.",
              },
              {
                q: "Can I export my data?",
                a: "Yes, every plan — including Free — includes a one-click CSV export of leads, transactions, and messages. Your data is yours. If you ever leave, you leave with everything.",
              },
            ].map((item, idx) => (
              <div key={item.q} className="rounded-xl bg-white ring-1 ring-slate-200 p-5" data-testid={`faq-${idx}`}>
                <dt className="font-heading font-bold text-slate-900 text-base">{item.q}</dt>
                <dd className="mt-2 text-sm text-slate-700 leading-relaxed">{item.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────── */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter">Ready to close more with less friction?</h2>
          <p className="mt-4 text-slate-300 leading-relaxed">Spin up your workspace in under two minutes. Free forever for your first three clients — no credit card required.</p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Link to="/auth" className="inline-flex items-center gap-2 rounded-md bg-white text-slate-900 hover:bg-slate-100 px-6 py-3 text-sm font-bold shadow-lg" data-testid="final-cta-primary">
              Get started free <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/auth?mode=login" className="inline-flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 ring-1 ring-white/25 text-white px-6 py-3 text-sm font-semibold" data-testid="final-cta-secondary">
              I already have an account
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────── */}
      <footer className="bg-white border-t border-slate-200 py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2.5">
            <img src="/ikey-logo.png" alt="iKEY" className="h-7 w-7 rounded-md ring-1 ring-slate-200" />
            <span>© {new Date().getFullYear()} iKEY, Inc. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/about" className="hover:text-slate-900" data-testid="footer-about">About</Link>
            <Link to="/pricing" className="hover:text-slate-900" data-testid="footer-pricing">Pricing</Link>
            <Link to="/best-crm-for-solo-agents" className="hover:text-slate-900" data-testid="footer-solo">Solo Agent Hub</Link>
            <Link to="/vs/follow-up-boss" className="hover:text-slate-900" data-testid="footer-vs-fub">vs Follow Up Boss</Link>
            <Link to="/vs/wise-agent" className="hover:text-slate-900" data-testid="footer-vs-wa">vs Wise Agent</Link>
            <Link to="/vs/kvcore" className="hover:text-slate-900" data-testid="footer-vs-kv">vs kvCORE</Link>
            <Link to="/vs/top-producer" className="hover:text-slate-900" data-testid="footer-vs-tp">vs Top Producer</Link>
            <Link to="/terms" className="hover:text-slate-900" data-testid="footer-terms">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-slate-900" data-testid="footer-privacy">Privacy Policy</Link>
            <a href="mailto:support@ikey.app" className="hover:text-slate-900">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
